// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ComponentProps } from 'react';
import { Image, type ImageStyle, type StyleProp } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

import {
  ChipInIcon,
  hazardScoreInputSymbol,
  MysteryPrizeIcon,
  type HazardSymbolType,
} from '@/features/hole/assets';
import { drawnScoreInput } from '@/theme/chrome';
import appColors from '@/theme/colors';

/** Everything the score input draws a symbol for: the five hazards, plus chip in and mystery. */
export type ScoreSymbolType = HazardSymbolType | 'chipIn' | 'mystery';

type GlyphName = ComponentProps<typeof MaterialCommunityIcons>['name'];

/**
 * One glyph per symbol, for skins whose score input is drawn rather than painted. They stand in
 * for the animals one for one so the column keeps its shape and its reading order: a waterline,
 * a beach, a warning, someone walking off, a flag still on the green, a ball dropping, a prize.
 */
const GLYPH_BY_TYPE: Record<ScoreSymbolType, GlyphName> = {
  water: 'waves',
  bunker: 'beach',
  penalty: 'alert-circle-outline',
  out: 'exit-run',
  threeputt: 'flag-variant-outline',
  chipIn: 'golf',
  mystery: 'gift-outline',
};

function artworkFor(type: ScoreSymbolType, animated: boolean) {
  if (type === 'chipIn') return ChipInIcon;
  if (type === 'mystery') return MysteryPrizeIcon;
  return hazardScoreInputSymbol(type, animated);
}

type Props = {
  type: ScoreSymbolType;
  /** Whether the player has hazard animations on; only the painted set has a still and a moving cut. */
  animated?: boolean;
  /** Edge of the box the symbol fills. The drawn glyph is sized from it, the artwork by `style`. */
  size?: number;
  style?: StyleProp<ImageStyle>;
};

/**
 * The symbol for one score-input row: the painted animal where the skin ships artwork, a palette
 * glyph where it does not. Both sit in the same 52x52 wrap, so callers need not care which.
 */
export function HazardSymbol({ type, animated = false, size = 52, style }: Props) {
  if (drawnScoreInput) {
    return (
      <MaterialCommunityIcons
        name={GLYPH_BY_TYPE[type]}
        size={Math.round(size * 0.56)}
        color={appColors.text.secondary}
      />
    );
  }
  return <Image source={artworkFor(type, animated)} style={style} resizeMode="contain" />;
}

export default HazardSymbol;
